import React, { useMemo } from 'react';
import moment from 'moment';
import { PluginDetailsProps } from './types';
import { styles } from './styles';
import { CollapsiblePanel, DetailField } from '@v2/components';
import PluginVersionCard from '../PluginVersionCard';
import { styles as cardStyles } from '../PluginVersionCard/styles';

const formatReleaseDate = (releaseDate: string): string =>
  moment.utc(releaseDate).local().format('M/D/YYYY LT');

const getPluginGeneralStatus = (discreteStatus: {
  isCached: boolean;
  isInstalled: boolean;
  isRunning: boolean;
}): string => {
  if (discreteStatus?.isRunning) return 'Running';
  if (discreteStatus?.isInstalled) return 'Installed';
  if (discreteStatus?.isCached) return 'Cached';
  return 'Present in Manifest';
};

const PluginDetails: React.FC<PluginDetailsProps> = ({
  plugin,
  versions,
  versionsLoading,
  pims,
}) => {
  const defaultVersion = plugin.defaultVersion;
  const defaultStatus = plugin.defaultStatus;
  const activeVersions = versions.filter((v) => !v.IsInactive);

  const pimsLookup = useMemo(
    () => new Map(pims.map((p) => [p.id, p.name])),
    [pims],
  );

  return (
    <div className={styles.fieldContainer}>
      <DetailField label="Name">{plugin.name}</DetailField>
      <DetailField label="UID">{plugin.id}</DetailField>
      <DetailField label="Default startup timeout">{plugin.defaultStartupTimeout}</DetailField>
      <DetailField label="Default plugin version">
        {defaultVersion
          ? `${defaultVersion.version} (${formatReleaseDate(defaultVersion.releaseDate)})`
          : '-'}
      </DetailField>
      <DetailField label="Active status">
        {plugin.isInactive ? 'Inactive' : 'Active'}
      </DetailField>
      <DetailField label="Default status">
        {getPluginGeneralStatus(defaultStatus)}
      </DetailField>
      <DetailField label="Scheduled release time">
        {plugin.soonestScheduledRelease ?? '-'}
      </DetailField>
      <div className={styles.fieldFullWidth}>
        <CollapsiblePanel
          title={`PIMS dependencies (${plugin.pimsDependencies?.length ?? 0})`}
          defaultExpanded={false}
        >
          {plugin.pimsDependencies?.length ? (
            <div className={styles.cardGrid}>
              {plugin.pimsDependencies.map((dep) => (
                <div key={dep.pimsId} className={cardStyles.container}>
                  <div className={cardStyles.info}>
                    <div className={cardStyles.name}>
                      {pimsLookup.get(dep.pimsId) ?? dep.pimsId}
                    </div>
                    {dep.minimumPimsVersionId && (
                      <div className={cardStyles.value}>Min: {dep.minimumPimsVersionId}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.emptyText}>None</div>
          )}
        </CollapsiblePanel>
      </div>
      <div className={styles.fieldFullWidth}>
        <CollapsiblePanel
          title={`Active versions (${activeVersions.length})`}
          defaultExpanded={false}
        >
          {versionsLoading ? (
            <div className={styles.emptyText}>Loading versions...</div>
          ) : activeVersions.length === 0 ? (
            <div className={styles.emptyText}>No active versions</div>
          ) : (
            <div className={styles.cardGrid}>
              {activeVersions.map((v) => (
                <PluginVersionCard key={v.id} version={v} />
              ))}
            </div>
          )}
        </CollapsiblePanel>
      </div>
    </div>
  );
};

export default PluginDetails;
